import type { EnforcementState } from './sanitizeSourceConfig';

export type CheckOtherSourcesBuckets = EnforcementState['sourceControl'];

export type CheckOtherSourcesOption = 'clinicalWeb' | 'clinicalSources' | 'myLibrary';

export type CheckOtherSourcesUpdates = {
  localGuidelines?: boolean;
  primaryEvidence?: boolean;
  myLibrary?: boolean;
  webSearch?: boolean;
};

const OFFERED_SOURCES: readonly CheckOtherSourcesOption[] = [
  'clinicalWeb',
  'clinicalSources',
  'myLibrary',
];

function isOfferedSource(source: string): source is CheckOtherSourcesOption {
  return (OFFERED_SOURCES as readonly string[]).includes(source);
}

function bucketForSource(
  source: CheckOtherSourcesOption,
  buckets: CheckOtherSourcesBuckets
): { enabled: boolean; changeable: boolean } {
  switch (source) {
    case 'clinicalWeb':
      return buckets.webCollections;
    case 'clinicalSources':
      return buckets.clinicalSources;
    case 'myLibrary':
      return buckets.library;
  }
}

export function isCheckOtherSourceOffered(
  source: CheckOtherSourcesOption,
  buckets: CheckOtherSourcesBuckets
): boolean {
  return bucketForSource(source, buckets).enabled;
}

function turnOffIfChangeable(
  updates: CheckOtherSourcesUpdates,
  key: keyof CheckOtherSourcesUpdates,
  changeable: boolean
): void {
  if (changeable) {
    updates[key] = false;
  }
}

export function resolveCheckOtherSourcesUpdates(
  source: string,
  buckets: CheckOtherSourcesBuckets
): CheckOtherSourcesUpdates | null {
  if (!isOfferedSource(source) || !isCheckOtherSourceOffered(source, buckets)) {
    return null;
  }

  const updates: CheckOtherSourcesUpdates = {};

  switch (source) {
    case 'clinicalSources':
      updates.localGuidelines = true;
      updates.primaryEvidence = true;
      turnOffIfChangeable(updates, 'myLibrary', buckets.library.changeable);
      turnOffIfChangeable(updates, 'webSearch', buckets.webCollections.changeable);
      break;
    case 'clinicalWeb':
      updates.webSearch = true;
      turnOffIfChangeable(
        updates,
        'localGuidelines',
        buckets.clinicalSources.changeable
      );
      turnOffIfChangeable(
        updates,
        'primaryEvidence',
        buckets.clinicalSources.changeable
      );
      turnOffIfChangeable(updates, 'myLibrary', buckets.library.changeable);
      break;
    case 'myLibrary':
      updates.myLibrary = true;
      turnOffIfChangeable(
        updates,
        'localGuidelines',
        buckets.clinicalSources.changeable
      );
      turnOffIfChangeable(
        updates,
        'primaryEvidence',
        buckets.clinicalSources.changeable
      );
      turnOffIfChangeable(updates, 'webSearch', buckets.webCollections.changeable);
      break;
  }

  return updates;
}
